import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { addDays, todayIso, type PlanningResponse } from "@klantpunt/shared";
import { api } from "../../lib/api";
import { hasModule, useMe } from "../../lib/auth";
import { PlanningLineDialog } from "./PlanningLineDialog";
import { appointmentText, toRows, type PlanRow } from "./planningView";

/** Venster van 120 dagen: zo ver kijkt *Meenemen in hetzelfde bezoek* vooruit (`bundleCandidates`). */
const HORIZON_DAYS = 120;

/**
 * Planningsregels voor inplannen buiten de pagina Planning (bij de asset, het gebouw of de keuringenlijst).
 * Zonder `buildingId` alle gebouwen die de gebruiker ziet. Valt onder de sleutel `planning`, zodat opslaan in
 * het inplanvenster ook deze regels ververst.
 */
export function usePlanRows(buildingId?: string | null) {
  const { data: me } = useMe();
  const today = todayIso();
  const to = addDays(today, HORIZON_DAYS);
  return useQuery({
    queryKey: ["planning", "bron", buildingId ?? "org", today],
    queryFn: () =>
      api<PlanningResponse>(`/planning?from=${today}&to=${to}${buildingId ? `&buildingId=${buildingId}` : ""}`),
    enabled: hasModule(me, "assets", "view"),
    retry: false,
    select: (d) => toRows(d.lines, today, to, today),
  });
}

/** Regel van een uitvoering (`asset_obligations.id`) in de planningsregels. */
export const obligationRow = (rows: PlanRow[] | undefined, obligationId: string) =>
  rows?.find((r) => r.l.source === "obligation" && r.l.refId === obligationId);

/** "18 nov 2026, voorlopig · Kone" */
export const appointmentLine = (row: PlanRow) => {
  const a = row.l.appointment;
  if (!a) return "";
  return `${appointmentText(a)}${a.performer?.name ? ` · ${a.performer.name}` : ""}`;
};

/**
 * Het inplanvenster van de planning voor één uitvoering. Wacht tot de regel er is (na *Toevoegen en
 * inplannen* moet de planning eerst opnieuw geladen worden); bestaat hij niet, dan sluit het venster.
 */
export function PlanObligationDialog({
  buildingId,
  obligationId,
  onClose,
}: {
  buildingId: string;
  obligationId: string;
  onClose: () => void;
}) {
  const q = usePlanRows(buildingId);
  const row = obligationRow(q.data, obligationId);
  const missing = !q.isFetching && (q.isError || (q.isSuccess && !row));
  useEffect(() => {
    if (missing) onClose();
  }, [missing, onClose]);
  if (!row || !q.data) return null;
  return <PlanningLineDialog row={row} rows={q.data} onClose={onClose} sourceLink={false} />;
}
